import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getDamageCases, getDamageCase, addCaseNote, decideCase } from '../api/admin';
import { useAuth } from '../context/AuthContext';
import styles from './DamageCases.module.css';

const STATUS_FILTERS = ['all', 'open', 'awaiting_evidence', 'closed'];

function badgeClass(status) {
  if (status === 'closed') return 'badgeClosed';
  if (status === 'open') return 'badgeOpen';
  return 'badgeDefault';
}

export default function DamageCases() {
  const qc = useQueryClient();
  const { user: adminUser } = useAuth();
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [decision, setDecision] = useState({ value: '', amount: '', reason: '' });

  const { data: listData, isLoading, error: listError, isError: listIsError } = useQuery({
    queryKey: ['damage-cases'],
    queryFn: () => getDamageCases().then((r) => r.data),
  });

  const { data: caseData, isLoading: caseLoading } = useQuery({
    queryKey: ['damage-case', selectedId],
    queryFn: () => getDamageCase(selectedId).then((r) => r.data),
    enabled: !!selectedId,
  });

  const noteMutation = useMutation({
    mutationFn: ({ id, description }) => addCaseNote(id, description),
    onSuccess: () => {
      toast.success('Note added');
      qc.invalidateQueries({ queryKey: ['damage-case', selectedId] });
      setNoteText('');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed'),
  });

  const decideMutation = useMutation({
    mutationFn: ({ id, body }) => decideCase(id, body),
    onSuccess: () => {
      toast.success('Case decided');
      qc.invalidateQueries({ queryKey: ['damage-case', selectedId] });
      qc.invalidateQueries({ queryKey: ['damage-cases'] });
      setDecision({ value: '', amount: '', reason: '' });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Decision failed'),
  });

  const cases = (listData?.cases || []).filter((c) => filter === 'all' || c.status === filter);
  const isDirector = adminUser?.user_role === 'company_director';
  const isClosed = caseData?.status === 'closed';

  return (
    <div className={styles.wrapper}>
      <h1 className={styles.title}>Damage Cases</h1>

      <div className={styles.filters}>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            className={`${styles.filterBtn} ${filter === f ? styles.filterActive : ''}`}
            onClick={() => setFilter(f)}
          >
            {f.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <div className={styles.layout}>
        <div className={styles.listPanel}>
          {isLoading && <p>Loading…</p>}
          {listIsError && (
            <div className={styles.empty} style={{ background: '#fef2f2', color: '#991b1b', padding: 12, borderRadius: 8, border: '1px solid #fecaca' }}>
              <strong>Could not load damage cases:</strong> {listError?.response?.data?.error || listError?.message || 'Unknown error'}.
              {listError?.response?.status === 403 && (
                <> Your role <code>{adminUser?.user_role || 'unknown'}</code> may need <code>user_role = 'admin'</code>. Contact the managing director to check your admin permissions.</>
              )}
            </div>
          )}
          {!isLoading && !listIsError && cases.length === 0 && <p className={styles.empty}>No cases found.</p>}
          {cases.map((c) => (
            <div
              key={c.id}
              className={`${styles.caseRow} ${selectedId === c.id ? styles.caseRowActive : ''}`}
              onClick={() => setSelectedId(c.id)}
            >
              <div className={styles.caseRowTop}>
                <span className={styles.caseId}>Case #{c.id.slice(0, 8)}</span>
                <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {c.country_code && <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', borderRadius: 999, padding: '2px 8px' }}>{c.country_code}</span>}
                  <span className={styles[badgeClass(c.status)]}>{c.status.replace(/_/g, ' ')}</span>
                </span>
              </div>
              <div className={styles.caseRowInfo}>
                {c.bookings?.vehicles && (
                  <span>{c.bookings.vehicles.make} {c.bookings.vehicles.model} · {c.bookings.vehicles.license_plate}</span>
                )}
                {c.created_at && (
                  <small style={{ color: '#64748b', display: 'block', marginTop: 2 }}>Reported {new Date(c.created_at).toLocaleString()}</small>
                )}
              </div>
              <div className={styles.caseRowParties}>
                <span>Owner: {c.owner?.full_name || '—'}</span>
                <span>Renter: {c.renter?.full_name || '—'}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.detailPanel}>
          {!selectedId && <p className={styles.empty}>Select a case to review.</p>}
          {selectedId && caseLoading && <p>Loading case…</p>}
          {selectedId && !caseLoading && (
            <>
              <div className={styles.detailHeader}>
                <h2>Case #{caseData.id.slice(0, 8)}</h2>
                <span className={styles[badgeClass(caseData.status)]}>{caseData.status.replace(/_/g, ' ')}</span>
              </div>
              {caseData.booking_id && (
                <p className={styles.muted} style={{ marginTop: -4 }}>
                  Booking ref: <code>{String(caseData.booking_id).slice(0, 12)}…</code>
                  {caseData.handover_protocol_id && <> · Handover: <code>{String(caseData.handover_protocol_id).slice(0, 12)}…</code></>}
                  {caseData.return_protocol_id && <> · Return: <code>{String(caseData.return_protocol_id).slice(0, 12)}…</code></>}
                </p>
              )}

              {caseData.reasons?.length > 0 && (
                <div className={styles.section}>
                  <h3>Reasons</h3>
                  {caseData.reasons.map((r, i) => (
                    <div key={i} className={styles.reasonItem}>
                      <strong>{r.type.replace(/_/g, ' ')}</strong>
                      {r.count && <span> ({r.count})</span>}
                      {r.delta_km && <span> — {r.delta_km} km</span>}
                      {r.note && <span> — {r.note}</span>}
                      {r.description && <span> — {r.description}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Inspection damage evidence — what was reported in DepartureInspection and shown on the client DamageCase.jsx page. */}
              {(caseData.inspection_damage_notes?.length > 0 || caseData.inspection_damage_photos?.length > 0) && (
                <div className={styles.section}>
                  <h3>Reported Damage Evidence (Inspections)</h3>
                  {caseData.inspection_damage_notes?.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <h4 style={{ fontSize: 13, marginTop: 0, marginBottom: 6, color: '#334155' }}>Damage descriptions (from vehicle inspection forms)</h4>
                      {caseData.inspection_damage_notes.map((n, i) => (
                        <div key={i} className={styles.submission} style={{ borderLeft: '3px solid #f59e0b' }}>
                          <div className={styles.subMeta}>
                            {String(n.step || 'inspection')} · {new Date(n.created_at).toLocaleString()} · insp {String(n.inspection_id || '').slice(0, 8)}…
                          </div>
                          <p>{n.notes}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {caseData.inspection_damage_photos?.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: 13, marginTop: 0, marginBottom: 6, color: '#334155' }}>Damage photos (with tamper-evidence timestamps)</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                        {caseData.inspection_damage_photos.map((p, i) => (
                          <div key={i} style={{ position: 'relative' }}>
                            <a href={p.url} target="_blank" rel="noreferrer">
                              <img src={p.url} alt="" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0', display: 'block' }} />
                            </a>
                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, lineHeight: 1.35, wordBreak: 'break-word' }}>
                              {p.step} · {new Date(p.created_at || p.ts_server || new Date()).toLocaleDateString()}
                              {p.client_gps_lat != null && p.client_gps_lng != null && (
                                <span style={{ display: 'block' }}>
                                  📍 {Number(p.client_gps_lat).toFixed(5)}, {Number(p.client_gps_lng).toFixed(5)}
                                </span>
                              )}
                              {p.server_uploaded_at && <span style={{ display: 'block' }}>🖥 Server: {new Date(p.server_uploaded_at).toLocaleTimeString()}</span>}
                              {p.client_uploaded_at && <span style={{ display: 'block' }}>📱 Client: {new Date(p.client_uploaded_at).toLocaleTimeString()}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className={styles.section}>
                <h3>Submissions & Notes</h3>
                {caseData.submissions?.length === 0 && <p className={styles.muted}>None yet.</p>}
                {caseData.submissions?.map((s) => (
                  <div key={s.id} className={styles.submission}>
                    <div className={styles.subMeta}>
                      {s.party} · {s.kind} · {new Date(s.created_at).toLocaleString()}
                    </div>
                    <p>{s.description}</p>
                    {s.estimated_cost != null && <p className={styles.muted}>Estimated: ${s.estimated_cost}</p>}
                    {Array.isArray(s.documents) && s.documents.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <span className={styles.muted} style={{ fontSize: 12 }}>Evidence docs ({s.documents.length}):</span>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 6, marginTop: 4 }}>
                          {s.documents.map((u, i) => (
                            <a key={i} href={u} target="_blank" rel="noreferrer">
                              <img src={u} alt="" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 4, border: '1px solid #e2e8f0' }} />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {caseData.events?.length > 0 && (
                <div className={styles.section}>
                  <h3>Events</h3>
                  {caseData.events.map((e) => (
                    <div key={e.id} className={styles.eventRow}>
                      <span className={styles.eventLabel}>{e.event.replace(/_/g, ' ')}</span>
                      <span className={styles.muted}>{new Date(e.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}

              {!isClosed && (
                <div className={styles.section}>
                  <h3>Add Note</h3>
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Internal note…"
                    className={styles.textarea}
                    rows={2}
                  />
                  <button
                    className={styles.btnSecondary}
                    disabled={!noteText.trim() || noteMutation.isPending}
                    onClick={() => noteMutation.mutate({ id: selectedId, description: noteText })}
                  >
                    {noteMutation.isPending ? 'Adding…' : 'Add Note'}
                  </button>
                </div>
              )}

              {!isClosed && isDirector && (
                <div className={styles.section}>
                  <h3>Managing Director Decision</h3>
                  <select
                    value={decision.value}
                    onChange={(e) => setDecision({ ...decision, value: e.target.value })}
                    className={styles.select}
                  >
                    <option value="">Select decision…</option>
                    <option value="rejected">Rejected — no charge</option>
                    <option value="partially_approved">Partially Approved</option>
                    <option value="approved">Approved — full deposit</option>
                    <option value="external_review">Send to External Review</option>
                  </select>
                  {['partially_approved', 'approved'].includes(decision.value) && (
                    <input
                      type="number"
                      value={decision.amount}
                      onChange={(e) => setDecision({ ...decision, amount: e.target.value })}
                      placeholder="Amount to capture (USD)"
                      className={styles.input}
                      step="0.01"
                    />
                  )}
                  <input
                    type="text"
                    value={decision.reason}
                    onChange={(e) => setDecision({ ...decision, reason: e.target.value })}
                    placeholder="Decision reason"
                    className={styles.input}
                  />
                  <button
                    className={styles.btnPrimary}
                    disabled={!decision.value || decideMutation.isPending}
                    onClick={() => decideMutation.mutate({
                      id: selectedId,
                      body: {
                        decision: decision.value,
                        approved_amount: Number(decision.amount) || 0,
                        decision_reason: decision.reason,
                      },
                    })}
                  >
                    {decideMutation.isPending ? 'Deciding…' : 'Decide Case'}
                  </button>
                </div>
              )}

              {!isClosed && !isDirector && (
                <p className={styles.muted}>Only the Managing Director can decide this case.</p>
              )}

              {isClosed && caseData.decision && (
                <div className={styles.decisionBox}>
                  <h3>Decision: {caseData.decision.replace(/_/g, ' ')}</h3>
                  {caseData.approved_amount > 0 && <p>Approved: ${caseData.approved_amount}</p>}
                  {caseData.decision_reason && <p className={styles.muted}>{caseData.decision_reason}</p>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
